'use client';

import { c as _c } from "react/compiler-runtime";
import 'swiper/css';
import 'swiper/css/pagination';
import { useGSAP } from '@gsap/react';
import { clsx } from 'clsx';
import { FC, useCallback, useRef, useState } from 'react';
import type { Swiper as SwiperType } from 'swiper';
import { A11y, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { ServicesLobbyCardsDto } from '@/components/ServicesLobbyPage/ServicesLobbyCards/types';
import StrapiRichContent from '@/components/StrapiRichContent';
import { gsap } from '@/lib/gsap';
import Card from './Card';
import { SWIPER_BREAKPOINTS, SWIPER_SPACE_BETWEEN } from './const';
import { SERVICES_LOBBY_CARDS_LOTTIES } from './lottie';
import css from './style.module.scss';
interface Props {
  data: ServicesLobbyCardsDto;
}
const ServicesLobbyCards: FC<Props> = t0 => {
  const $ = _c(22);
  const {
    data
  } = t0;
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const [showPagination, setShowPagination] = useState(false);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const wrapper = sectionRef.current;
      const titleEl = titleRef.current;
      const titleLines = titleEl?.querySelectorAll("p");
      const slides = wrapper?.querySelectorAll(`.${css.slide}`);
      if (!wrapper || !titleEl) {
        return;
      }
      const mm = gsap.matchMedia();
      mm.add({
        isDesktop: "(min-width: 992px)",
        isMobile: "(max-width: 991px)"
      }, context => {
        const {
          isDesktop
        } = context.conditions as gsap.Conditions;
        const titleTargets = titleLines && titleLines.length ? Array.from(titleLines) : [titleEl];
        gsap.set(titleTargets, {
          opacity: 0,
          y: 40,
          filter: "blur(10px)"
        });
        gsap.to(titleTargets, {
          opacity: 1,
          y: 0,
          filter: "blur(0px)",
          duration: 1.1,
          ease: "power3.out",
          stagger: titleTargets.length > 1 ? 0.15 : 0,
          scrollTrigger: {
            trigger: titleEl,
            start: "top 80%",
            toggleActions: "play none none reverse"
          }
        });
        if (isDesktop && slides?.length) {
          gsap.from(slides, {
            y: 100,
            opacity: 0,
            stagger: 0.25,
            duration: 1,
            ease: "power3.out",
            scrollTrigger: {
              trigger: wrapper,
              start: "top 50%"
            }
          });
        }
      });
      return () => {
        mm.revert();
      };
    };
    t2 = {
      scope: sectionRef
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useGSAP(t1, t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = swiper => {
      setShowPagination(!swiper.isLocked);
      swiper.pagination?.update?.();
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const syncSwiper = t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = swiper_0 => {
      syncSwiper(swiper_0);
      swiper_0.pagination?.render?.();
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const handleSwiperInit = t4;
  let t5;
  if ($[4] !== data.title) {
    t5 = <div className="container"><h2 ref={titleRef} className={css.title}><StrapiRichContent content={data.title} /></h2></div>;
    $[4] = data.title;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [Pagination, A11y];
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      el: `.${css.pagination}`,
      clickable: true
    };
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== data.cards) {
    t8 = data.cards.map(_temp);
    $[8] = data.cards;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== t8) {
    t9 = <Swiper watchOverflow={true} grabCursor={true} speed={850} modules={t6} className={css.cardsSwiper} spaceBetween={SWIPER_SPACE_BETWEEN} slidesPerView={1.1} breakpoints={SWIPER_BREAKPOINTS} pagination={t7} onSwiper={handleSwiperInit} onResize={syncSwiper} onBreakpoint={syncSwiper} onUpdate={syncSwiper}>{t8}</Swiper>;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const t10 = !showPagination && css.paginationHidden;
  let t11;
  if ($[12] !== t10) {
    t11 = clsx(css.pagination, t10);
    $[12] = t10;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== t11) {
    t12 = <div className="container"><div className={t11} /></div>;
    $[14] = t11;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] !== t12 || $[17] !== t9) {
    t13 = <div className={css.sliderWrap}>{t9}{t12}</div>;
    $[16] = t12;
    $[17] = t9;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== t13 || $[20] !== t5) {
    t14 = <section ref={sectionRef} className={css.section}>{t5}{t13}</section>;
    $[19] = t13;
    $[20] = t5;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  return t14;
};
export default ServicesLobbyCards;
function _temp(card, index) {
  return <SwiperSlide key={index} className={css.slide}><Card lottie={SERVICES_LOBBY_CARDS_LOTTIES[index]} title={card.title} description={card.description} /></SwiperSlide>;
}